import { gql } from '@apollo/client';

import { USER_BADGE_FIELDS } from './queries';

const PUBLIC_USER_FIELDS = gql`
  ${USER_BADGE_FIELDS}
  fragment PublicUserFields on PublicUser {
    asfId
    firstName
    lastName
    displayName
    slug
    avatarUrl
    bio
    city
    region
    followerCount
    isHumanVerified
    isDisabled
    badges {
      ...UserBadgeFields
    }
  }
`;

const CONNECTION_FIELDS = gql`
  ${PUBLIC_USER_FIELDS}
  fragment ConnectionFields on Connection {
    id
    status
    requestMessage
    requestSpamScore
    requestSpamReasons
    createdAt
    acceptedAt
    respondedAt
    otherUser {
      ...PublicUserFields
    }
    requestedBy {
      asfId
      displayName
      slug
      avatarUrl
    }
  }
`;

export const CONNECTION_QUERY = gql`
  ${CONNECTION_FIELDS}
  query Connection($userId: ID!) {
    connection(userId: $userId) {
      ...ConnectionFields
    }
  }
`;

export const CONNECTIONS_QUERY = gql`
  ${CONNECTION_FIELDS}
  query Connections($status: ConnectionStatus, $first: Int, $after: String) {
    connections(status: $status, first: $first, after: $after) {
      edges {
        cursor
        node {
          ...ConnectionFields
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export const INCOMING_CONNECTION_REQUESTS_QUERY = gql`
  ${CONNECTION_FIELDS}
  query IncomingConnectionRequests($first: Int, $after: String) {
    incomingConnectionRequests(first: $first, after: $after) {
      edges {
        cursor
        node {
          ...ConnectionFields
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const OUTGOING_CONNECTION_REQUESTS_QUERY = gql`
  ${CONNECTION_FIELDS}
  query OutgoingConnectionRequests($first: Int, $after: String) {
    outgoingConnectionRequests(first: $first, after: $after) {
      edges {
        cursor
        node {
          ...ConnectionFields
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const ARE_CONNECTED_QUERY = gql`
  query AreConnected($userId: ID!) {
    areConnected(userId: $userId)
  }
`;

export const ARE_CONNECTED_BULK_QUERY = gql`
  query AreConnectedBulk($userIds: [ID!]!) {
    areConnectedBulk(userIds: $userIds)
  }
`;

export const CONNECTION_STATES_QUERY = gql`
  query ConnectionStates($userIds: [ID!]!) {
    connectionStates(userIds: $userIds) {
      userId
      connectionId
      status
      requestedByViewer
      isBlockedByViewer
    }
  }
`;

export const MESSAGEABLE_USERS_QUERY = gql`
  ${PUBLIC_USER_FIELDS}
  query MessageableUsers($query: String, $first: Int, $after: String) {
    messageableUsers(query: $query, first: $first, after: $after) {
      edges {
        cursor
        node {
          ...PublicUserFields
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

// Member lookup across all of ArtHelper (public, discoverable profiles) — the half of
// "find a friend" that messageableUsers cannot answer, since it only searches connections.
export const SEARCH_MEMBERS_QUERY = gql`
  query SearchMembers($query: String!, $limit: Int) {
    searchProfiles(query: $query, limit: $limit) {
      id
      userId
      displayName
      slug
      avatarUrl
      isHumanVerified
    }
  }
`;

export const SEND_CONNECTION_REQUEST_MUTATION = gql`
  ${CONNECTION_FIELDS}
  mutation SendConnectionRequest($input: SendConnectionRequestInput!) {
    sendConnectionRequest(input: $input) {
      ...ConnectionFields
    }
  }
`;

export const SET_CONNECTION_REQUEST_MESSAGE_MUTATION = gql`
  ${CONNECTION_FIELDS}
  mutation SetConnectionRequestMessage($input: SetConnectionRequestMessageInput!) {
    setConnectionRequestMessage(input: $input) {
      ...ConnectionFields
    }
  }
`;

export const ACCEPT_CONNECTION_REQUEST_MUTATION = gql`
  ${CONNECTION_FIELDS}
  mutation AcceptConnectionRequest($connectionId: ID!) {
    acceptConnectionRequest(connectionId: $connectionId) {
      ...ConnectionFields
    }
  }
`;

export const ACCEPT_CONNECTION_BY_TOKEN_MUTATION = gql`
  ${CONNECTION_FIELDS}
  mutation AcceptConnectionByToken($token: String!) {
    acceptConnectionByToken(token: $token) {
      status
      connection {
        ...ConnectionFields
      }
    }
  }
`;

export const DECLINE_CONNECTION_REQUEST_MUTATION = gql`
  mutation DeclineConnectionRequest($connectionId: ID!) {
    declineConnectionRequest(connectionId: $connectionId)
  }
`;

export const WITHDRAW_CONNECTION_REQUEST_MUTATION = gql`
  mutation WithdrawConnectionRequest($connectionId: ID!) {
    withdrawConnectionRequest(connectionId: $connectionId)
  }
`;

export const REMOVE_CONNECTION_MUTATION = gql`
  mutation RemoveConnection($userId: ID!) {
    removeConnection(userId: $userId)
  }
`;

// The SPA's only block document. `source` records which surface the block was
// taken from on the Blocked members screen; it defaults to PROFILE server-side,
// so every caller here passes its own.
export const SUGGESTED_CONNECTION_SECTIONS_QUERY = gql`
  query SuggestedConnectionSections($sectionLimit: Int, $seed: String) {
    suggestedConnectionSections(sectionLimit: $sectionLimit, seed: $seed) {
      seed
      sections {
        axis
        totalCount
        artists {
          id
          userId
          slug
          displayName
          avatarUrl
          bio
          city
          country
          region
          user {
            asfId
            isHumanVerified
          }
          images(limit: 5) {
            id
            thumbnailUrl
            optimizedUrl
          }
          suggestedConnectionAxis
        }
      }
    }
  }
`;

export const SUGGESTED_CONNECTIONS_PAGE_QUERY = gql`
  query SuggestedConnectionsPage($axis: SuggestedConnectionAxis, $seed: String, $first: Int, $after: String) {
    suggestedConnectionsPage(axis: $axis, seed: $seed, first: $first, after: $after) {
      seed
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        axis
        node {
          id
          userId
          slug
          displayName
          avatarUrl
          bio
          city
          country
          region
          user {
            asfId
            isHumanVerified
          }
          images(limit: 5) {
            id
            thumbnailUrl
            optimizedUrl
          }
          suggestedConnectionAxis
        }
      }
    }
  }
`;

export const ENGAGED_WITH_YOU_QUERY = gql`
  query EngagedWithYou($limit: Int) {
    engagedWithYou(limit: $limit) {
      type
      targetType
      targetId
      targetHref
      at
      actor {
        userId
        name
        slug
        avatarUrl
      }
      artist {
        id
        userId
        slug
        displayName
        avatarUrl
        bio
        city
        country
        user {
          asfId
          isHumanVerified
        }
        images(limit: 5) {
          id
          thumbnailUrl
          optimizedUrl
        }
      }
    }
  }
`;

export const MUTUAL_CONNECTIONS_QUERY = gql`
  query MutualConnections($userIds: [ID!]!) {
    mutualConnections(userIds: $userIds) {
      userId
      count
      sample {
        asfId
        displayName
        firstName
        lastName
        avatarUrl
        slug
      }
    }
  }
`;

export const DISMISS_SUGGESTED_CONNECTION_MUTATION = gql`
  mutation DismissSuggestedConnection($input: DismissSuggestedConnectionInput!) {
    dismissSuggestedConnection(input: $input)
  }
`;

export const UNDISMISS_SUGGESTED_CONNECTION_MUTATION = gql`
  mutation UndismissSuggestedConnection($userId: ID!) {
    undismissSuggestedConnection(userId: $userId)
  }
`;

export const BLOCK_USER_MUTATION = gql`
  mutation BlockUser($userId: ID!, $source: BlockSource) {
    blockUser(userId: $userId, source: $source)
  }
`;

export const UNBLOCK_USER_MUTATION = gql`
  mutation UnblockUser($userId: ID!) {
    unblockUser(userId: $userId)
  }
`;
